import { useEffect, useState } from 'react';
import { useToast } from '../../../components/ToastContext';

const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const emptyForm = {
  name: '',
  start_date: '',
  end_date: '',
  days_of_week: [],
  price_type: 'FIXED',
  price_value: '',
  min_nights: 1,
  priority: 1,
  is_active: true,
};

const SeasonalPricingTab = ({ accessToken, propertyId, listFn, saveFn, deleteFn }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const toast = useToast();

  const load = async () => {
    if (!propertyId) return;
    setLoading(true);
    setError('');
    try {
      const rows = await listFn(accessToken, propertyId);
      setItems(rows || []);
    } catch (err) {
      setError(err.message || 'Failed to load rules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const toggleDay = (idx) => {
    setForm((f) => {
      const has = f.days_of_week.includes(idx);
      const next = has ? f.days_of_week.filter((d) => d !== idx) : [...f.days_of_week, idx];
      return { ...f, days_of_week: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!propertyId) {
      toast.error('Select a property first');
      return;
    }
    try {
      const payload = {
        ...form,
        property_id: propertyId,
      };
      if (!editingId) delete payload.id;
      const saved = await saveFn(accessToken, editingId ? { ...payload, id: editingId } : payload);
      setItems((prev) => {
        const filtered = prev.filter((i) => i.id !== saved.id);
        return [saved, ...filtered];
      });
      setForm(emptyForm);
      setEditingId(null);
      toast.success('Saved');
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setForm({
      ...emptyForm,
      ...row,
      price_type: row.price_type || (row.price_mode === 'percent' ? 'PERCENT' : 'FIXED'),
      price_value: row.price_value ?? row.value ?? '',
      days_of_week: row.days_of_week || [],
    });
  };

  const remove = async (row) => {
    if (!window.confirm('Delete this rule?')) return;
    await deleteFn(accessToken, row.id);
    setItems((prev) => prev.filter((i) => i.id !== row.id));
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div>
          <p className="text-lg font-semibold text-slate-900">Seasonal pricing</p>
          <p className="text-sm text-slate-600">Set date-bound price overrides without touching calendar overrides.</p>
        </div>
        {loading && <span className="text-xs text-slate-500">Loading...</span>}
      </div>
      {error && <div className="px-4 py-2 text-sm text-red-600">{error}</div>}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4">
        <form onSubmit={handleSubmit} className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold text-slate-900">{editingId ? 'Edit rule' : 'Add seasonal rule'}</p>
              <p className="text-xs text-slate-500">Higher priority wins; calendar overrides still take precedence.</p>
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
              Active
            </label>
          </div>
          <label className="text-sm text-slate-700 space-y-1">
            <span className="font-semibold">Name</span>
            <input
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="w-full rounded-lg border border-slate-200 px-3 py-2"
              placeholder="New Year Peak"
            />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Start date</span>
              <input
                type="date"
                required
                value={form.start_date}
                onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              />
            </label>
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">End date</span>
              <input
                type="date"
                required
                value={form.end_date}
                onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              />
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Price type</span>
              <select
                value={form.price_type}
                onChange={(e) => setForm((f) => ({ ...f, price_type: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              >
                <option value="FIXED">Fixed nightly price</option>
                <option value="PERCENT">Percent (+/-)</option>
              </select>
            </label>
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Value</span>
              <input
                type="number"
                step="0.1"
                value={form.price_value}
                onChange={(e) => setForm((f) => ({ ...f, price_value: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
                placeholder="2500"
              />
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Min nights</span>
              <input
                type="number"
                min="1"
                value={form.min_nights}
                onChange={(e) => setForm((f) => ({ ...f, min_nights: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              />
            </label>
            <label className="text-sm text-slate-700 space-y-1">
              <span className="font-semibold">Priority</span>
              <input
                type="number"
                min="1"
                value={form.priority}
                onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2"
              />
            </label>
          </div>
          <div className="text-sm text-slate-700 space-y-1">
            <span className="font-semibold">Days of week</span>
            <div className="flex flex-wrap gap-2">
              {dayLabels.map((d, idx) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => toggleDay(idx)}
                  className={`rounded-lg border px-2 py-1 text-xs ${
                    form.days_of_week.includes(idx) ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'border-slate-200 text-slate-700'
                  }`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button type="submit" className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700">
              {editingId ? 'Update rule' : 'Create rule'}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={() => {
                  setEditingId(null);
                  setForm(emptyForm);
                }}
                className="text-sm text-slate-600 underline"
              >
                Cancel edit
              </button>
            )}
          </div>
        </form>

        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div>
              <p className="text-lg font-semibold text-slate-900">Rules</p>
              <p className="text-xs text-slate-500">Priority then latest updated wins.</p>
            </div>
            <button className="text-xs text-emerald-700 underline" onClick={load}>
              Refresh
            </button>
          </div>
          <div className="divide-y divide-slate-100">
            {items.length === 0 && <div className="p-4 text-sm text-slate-600">No seasonal rules yet.</div>}
            {items.map((r) => (
              <div key={r.id} className="px-4 py-3 flex items-start justify-between hover:bg-slate-50">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-slate-900">{r.name || `Rule #${r.id}`}</p>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                      {r.price_type || r.price_mode} {r.price_value ?? r.value}
                    </span>
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700">Priority {r.priority}</span>
                  </div>
                  <p className="text-xs text-slate-500">
                    {r.start_date} → {r.end_date} • Min {r.min_nights || 1} nights
                  </p>
                  {r.days_of_week?.length ? (
                    <p className="text-xs text-slate-500">Days: {r.days_of_week.map((d) => dayLabels[d]).join(', ')}</p>
                  ) : (
                    <p className="text-xs text-slate-500">All days</p>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <label className="flex items-center gap-1 text-slate-600">
                    <input
                      type="checkbox"
                      checked={!!r.is_active}
                      onChange={(e) => saveFn(accessToken, { ...r, is_active: e.target.checked }).then(load)}
                    />
                    Active
                  </label>
                  <button className="text-emerald-700 underline" onClick={() => startEdit(r)}>
                    Edit
                  </button>
                  <button className="text-red-600 underline" onClick={() => remove(r)}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeasonalPricingTab;
